import { Dispatch, SetStateAction, useState } from "react";

import { TaskConstants } from "../../constants/workflow/TaskConstants";
import { TaskPanelData } from "../../models";
import { BaseResponseWithSingle, ServiceError, Status } from "../../service/Shared";
import {
  createWorkflowTask,
  CreateWorkflowTaskResponse,
  updateWorkflowTask,
  UpdateWorkflowTaskResponse,
} from "../../service/workflow";
import { convertDateToUtcStartOfDay, useIsLoadingWrapper } from "../../utils";
import { Toast } from "../../widget";
import { TaskPanelMode } from "./taskPanelMode";

interface UseTaskSubmitProps {
  mode: TaskPanelMode;
  isGeneralTask: boolean;
  currentTask: TaskPanelData;
  // Parent of a general task, written to objectType/objectUuid.
  generalTaskParent: { type: string | null; uuid: string | null };
  ownerOrganisationIsFixed: boolean;
  currentOrganisationUuid: string | null | undefined;
  // The audience to send, already resolved for the parent (pinned to the acting org where it has to be).
  getTaskVisibility: () => string[];
  // Tags and attachments are written after the task itself, since a new task has no uuid until then.
  saveTags: (taskUuid: string) => Promise<ServiceError[] | undefined>;
  saveAttachments: (taskUuid: string) => Promise<ServiceError[] | undefined>;
  setErrors: Dispatch<SetStateAction<ServiceError[] | undefined>>;
  setShowCompletePhaseModal: Dispatch<SetStateAction<boolean>>;
  handleRefresh: () => void;
  handleCloseTaskPanel: () => void;
}

interface UseTaskSubmitReturnData {
  handleSubmit: () => Promise<void>;
  isHandleSubmitLoading: boolean;
}

/*
  Saving the open task: the payload the two calls share, the few fields that differ between creating and
  updating, and the tag/attachment writes that can only run once the task has a uuid.
*/
export const useTaskSubmit = ({
  mode,
  isGeneralTask,
  currentTask,
  generalTaskParent,
  ownerOrganisationIsFixed,
  currentOrganisationUuid,
  getTaskVisibility,
  saveTags,
  saveAttachments,
  setErrors,
  setShowCompletePhaseModal,
  handleRefresh,
  handleCloseTaskPanel,
}: UseTaskSubmitProps): UseTaskSubmitReturnData => {
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);

  const handleSubmit = useIsLoadingWrapper(async (): Promise<void> => {
    setErrors([]);

    /*
      Reminders are only persisted when the toggle is on AND the task has an assignee (BR-02794) — the
      assignee can be cleared after a reminder was set, and its control is disabled at that point, so the
      requirement is enforced here rather than by mutating the form state. The picker is date-only, so the
      reminder is sent as midnight of the chosen day.
    */
    // Mirrors the panel's own check ("unassigned" is the placeholder key of the assignee select).
    const hasAssignee = currentTask.assignedToUserUuid !== null && currentTask.assignedToUserUuid !== "unassigned";
    const reminderDate =
      hasAssignee && currentTask.addReminder && currentTask.reminderDate
        ? convertDateToUtcStartOfDay(currentTask.reminderDate)
        : null;
    /*
      There is no delivery-type picker yet, so a task keeps the type it already has — overwriting it with
      the default would silently turn an existing "App" reminder into an email on any unrelated edit. The
      default only applies to a reminder being switched on without one.
    */
    const reminderType =
      hasAssignee && currentTask.addReminder
        ? (currentTask.reminderType ?? TaskConstants.TASK_REMINDER_TYPE_DEFAULT)
        : null;

    // General tasks link objectType/objectUuid to their parent; workflow tasks use it for the
    // optional activity relation (activity linking is out of scope for general tasks).
    const workflowObjectType = currentTask.relateToActivity ? "Activity" : null;

    // Everything both calls send identically — create and update differ only in the fields added below.
    const sharedPayload = {
      deadlineDate: currentTask.deadlineDate,
      description: currentTask.description,
      isMandatory: currentTask.isMandatory ?? false,
      isMilestone: currentTask.isMilestone ?? false,
      keyDate: currentTask.keyDate,
      name: currentTask.name ?? "",
      objectType: isGeneralTask ? generalTaskParent.type : workflowObjectType,
      objectUuid: isGeneralTask ? generalTaskParent.uuid : currentTask.activityUuid,
      reminderDate,
      reminderType,
      repeat: currentTask.repeat ?? false,
      visibility: getTaskVisibility(),
      ownerOrganisationUuid:
        ownerOrganisationIsFixed && currentOrganisationUuid
          ? currentOrganisationUuid
          : (currentTask.ownerOrganisationUuid ?? ""),
      workflowStepUuid: isGeneralTask ? null : (currentTask.stepUuid ?? ""),
    };

    let res: BaseResponseWithSingle<UpdateWorkflowTaskResponse | CreateWorkflowTaskResponse>;
    if (mode.kind === "edit") {
      // The task being edited, with the uuid and rowVersion an update needs — no optional access.
      const { task: editedTask } = mode;

      if (
        currentTask.status === TaskConstants.TASK_STATUS_COMPLETE &&
        (currentTask.resolution === null || currentTask.resolution.trim() === "")
      ) {
        setShowCompletePhaseModal(true);
        return;
      }

      res = await updateWorkflowTask({
        ...sharedPayload,
        assignedToUserUuid: currentTask.assignedToUserUuid !== "unassigned" ? currentTask.assignedToUserUuid : null,
        rowVersion: editedTask.rowVersion,
        resolution: currentTask.status === TaskConstants.TASK_STATUS_COMPLETE ? editedTask.resolution : null,
        status: currentTask.status ?? "",
        workflowTaskUuid: editedTask.uuid,
      });
    } else {
      res = await createWorkflowTask({
        ...sharedPayload,
        // A new task can only be assigned by the organisation that will own it.
        assignedToUserUuid:
          currentOrganisationUuid === currentTask.ownerOrganisationUuid &&
          currentTask.assignedToUserUuid !== "unassigned"
            ? currentTask.assignedToUserUuid
            : null,
        status: currentTask.status ?? "Unassigned",
      });
    }

    if (res.status === Status.Success && res.data) {
      // Two-phase tag link: existing tasks use their own uuid; new tasks use the created uuid.
      const resolvedTaskUuid =
        mode.kind === "edit"
          ? mode.task.uuid
          : ((res.data as CreateWorkflowTaskResponse).workflowTaskUuid ?? undefined);
      const tagErrors = resolvedTaskUuid ? await saveTags(resolvedTaskUuid) : undefined;
      /*
        Attachments against an existing task are already linked, so this only flushes the ones staged while
        the task was being created — the documents are uploaded by then, but had no task to link to.
      */
      const attachmentErrors = resolvedTaskUuid ? await saveAttachments(resolvedTaskUuid) : undefined;

      // The task itself is already persisted, so the panel must always be finalised here — keeping it
      // open would let a retry create a second task, or resubmit a now-stale rowVersion. A tag-link
      // failure is reported on its own and leaves the saved task untouched.
      Toast.success({
        message: `${isGeneralTask ? "General" : "Workflow"} task ${
          mode.kind === "edit" ? "updated" : "created"
        } successfully`,
      });
      if (tagErrors && tagErrors.length > 0) {
        Toast.error({ message: `Task saved, but its tags could not be updated: ${tagErrors[0].message}` });
      }
      if (attachmentErrors && attachmentErrors.length > 0) {
        Toast.error({ message: `Task saved, but its attachments could not be added: ${attachmentErrors[0].message}` });
      }
      handleRefresh();
      handleCloseTaskPanel();
    }

    if (res.status === Status.Error) {
      setErrors(res.errors);
    }
  }, setIsHandleSubmitLoading);

  return { handleSubmit, isHandleSubmitLoading };
};
